import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import Logo from "@/components/Logo";

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const { refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          refresh();
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 15) timer = setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    if (sessionId) poll();
    else setStatus("timeout");
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#08080A] text-[#F4F4F5] grain flex flex-col items-center justify-center px-6">
      <Logo />
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="mt-12 text-center max-w-md"
      >
        {status === "checking" && (
          <>
            <div className="w-12 h-12 mx-auto rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin mb-6" />
            <h1 className="font-display font-bold text-2xl mb-3" data-testid="payment-status-text">Confirming your payment...</h1>
            <p className="text-sm text-zinc-500">Locking in your seat at the table.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full emblem-coin flex items-center justify-center mb-6">
              <Check className="w-7 h-7 text-[#0A0A0C]" strokeWidth={3} />
            </div>
            <h1 className="font-display font-bold text-3xl mb-3" data-testid="payment-status-text">
              You're <span className="gold-text">in.</span>
            </h1>
            <p className="text-sm text-zinc-400 mb-8">Membership active. Your private Discord invite is waiting on the dashboard.</p>
            <button
              data-testid="payment-success-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="btn-gold inline-flex items-center gap-2 font-sub text-sm px-8 py-4 rounded-full"
            >
              Claim Your Access <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="font-display font-bold text-2xl mb-3" data-testid="payment-status-text">Still processing</h1>
            <p className="text-sm text-zinc-400 mb-8">Payment confirmations can take a moment. Check your dashboard — it updates automatically.</p>
            <button
              data-testid="payment-success-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="btn-ghost-gold inline-flex items-center gap-2 font-sub font-semibold text-sm px-8 py-4 rounded-full"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#08080A] text-[#F4F4F5] grain flex flex-col items-center justify-center px-6">
      <Logo />
      <motion.div
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="mt-12 text-center max-w-md"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-[#15151A] border border-[#26262F] flex items-center justify-center mb-6">
          <X className="w-7 h-7 text-zinc-500" strokeWidth={2.5} />
        </div>
        <h1 className="font-display font-bold text-2xl mb-3" data-testid="payment-cancel-text">Checkout canceled</h1>
        <p className="text-sm text-zinc-400 mb-8">No charge was made. The Cartel will be here when you're ready.</p>
        <Link
          data-testid="payment-cancel-home-btn"
          to="/"
          className="btn-ghost-gold inline-flex items-center gap-2 font-sub font-semibold text-sm px-8 py-4 rounded-full"
        >
          Back to the Surface
        </Link>
      </motion.div>
    </div>
  );
}
